import React, { Fragment, useEffect, useRef } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";
import { useSelector, useDispatch } from "react-redux";
import { resetService, setService } from "rtkSlices/ServiceCreatorSlice";
import { setRole } from "rtkSlices/metaDataSlice";
import { useCreateServiceMutation } from "services/services";
import { waitForSolutionInRole } from "utils/waitForSolutionInRole";
import { canOfferShare } from "utils/sharingSwitches";
import { buildServicePayload } from "utils/buildServicePayload";
import ShareTable from "components/Service Management/Share/ShareTable";
import { ShareConfirmation, ShareResults } from "components/Service Management/Share/ShareConfirmation";
import { ShareBusy } from "components/Service Management/Share";
import LeaveGuard from "components/Service Management/Share/LeaveGuard";
import { useShareFlow } from "components/Service Management/Share/useShareFlow";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";

// Final step of the create wizard. It CREATES the solution (D29 (b): this used to happen
// when the removed Device step opened). The create runs once per wizard run: only while
// the stored serviceId is empty, and at most once per mount. The "Creating Solution..."
// spinner stays until the server answers. On success: the server's message, the new
// serviceId stored, the role re-read so the new solution appears in the menus, and only
// then the success message. On error: the server's message and back to Key Info with the
// form kept. Solution sharing (plan stage 7, O21): the role is re-read until it holds the new solution
// (the creation worker writes the creator's role last, at most 2 minutes; the re-read continues after
// the screen is left), and, for users allowed to share, the share table sits under the success message:
// "Finish without sharing" or "Share Access with Roles (n)" (O25 labels), a confirmation pop-up, then the results.
export default function Done(props) {
  const dispatch = useDispatch();
  let token = window.localStorage.getItem("token");
  const { enqueueSnackbar } = useSnackbar();
  const serviceValue = useSelector((state) => state.serviceCreator);
  const metaDataValue = useSelector((state) => state.metaData);
  const [createService, serviceResult] = useCreateServiceMutation();
  const createRequested = useRef(false);
  const created = serviceValue.serviceId != "";
  const createdId = serviceResult.isSuccess ? serviceResult.data?.payload?._id : null;
  // A finite creator also needs the create answer to carry the stored creator (an older
  // serviceCreatorMS does not store it, so the server would refuse the share).
  const canShare =
    !!createdId &&
    canOfferShare({
      sharingOn: metaDataValue.solutionSharing === true,
      grantAll: !!metaDataValue.unscopedAdmin,
      ownModules: metaDataValue.apps,
      isCreator: !!serviceResult.data?.payload?.userId,
      groupLimited: false,
    });
  const share = useShareFlow({ serviceId: createdId, retryNotReady: true, active: canShare });
  const busy = share.phase === "sharing";
  const shared = share.phase === "result";
  const sentAny = share.results.length > 0; // something was sent: "Finish", not "Finish without sharing"

  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar(
      { title, message: message ? message : "Something went wrong", variant },
      { timeOut }
    );
  }

  useEffect(() => {
    if (serviceValue.serviceId == "" && !createRequested.current) {
      createRequested.current = true;
      const body = buildServicePayload(serviceValue, { mode: "create" });
      createService({ token, body });
    }
  }, []);

  useEffect(() => {
    if (serviceResult.isSuccess) {
      showSnackbar("Solution", serviceResult.data?.message, "success", 1000);
      dispatch(
        setService({
          serviceId: serviceResult.data.payload._id,
        })
      );
    }
    if (serviceResult.isError) {
      showSnackbar(
        "Solution",
        serviceResult.error?.data?.message,
        "error",
        1000
      );
      dispatch(
        setService({
          page: 0,
        })
      );
    }
  }, [serviceResult]);

  // Not cancelled when the screen goes: finishing never waits for it, and the menus still get the new
  // solution when it lands (validation V1).
  // It stops, and changes nothing, once another user or role is signed in on this tab (sign-out stays
  // inside the app).
  useEffect(() => {
    if (!createdId) return;
    const sid = String(createdId);
    const user = window.localStorage.getItem("user");
    const role = window.localStorage.getItem("role");
    const sameSession = () => window.localStorage.getItem("user") === user && window.localStorage.getItem("role") === role;
    waitForSolutionInRole(dispatch, role, createdId, { shouldStop: () => !sameSession() }).then((payload) => {
      if (!sameSession()) return;
      if (payload) dispatch(setRole(payload));
      const holds = !!(payload && (payload.services || []).some((s) => s && (String(s.serviceId) === sid || String(s.serviceId) === "*")));
      if (!holds) showSnackbar("Solution", "The new solution appears in the menus after a page reload.", "info", 4000);
    });
  }, [createdId]);

  const finish = () => {
    dispatch(resetService());
    props.history.push("/solutions/catalogue");
  };
  const color = metaDataValue.branding.secondaryColor;

  return (
    <div>
      <LeaveGuard active={busy} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: canShare ? "flex-start" : "center",
          flexDirection: "column",
          gap: canShare ? "8px" : "20px",
          maxHeight: "calc(100vh - 310px)",
          minHeight: "calc(100vh - 310px)",
          overflowY: "auto",
        }}
      >
        {created ? (
          <Fragment>
            <CheckCircleOutlineIcon style={{ height: canShare ? "4rem" : "10rem", width: canShare ? "4rem" : "10rem", color }} />
            <Typography sx={{ color, fontSize: canShare ? 20 : 24, fontWeight: 600 }} data-testid="solution-created-heading">
              Solution Created Successfully!
            </Typography>
            <p style={{ color, margin: 0 }}>
              <b>{`${serviceValue.name} has been configured and added successfully.`}</b>
            </p>
            {canShare && (
              <div style={{ width: "100%", maxWidth: 960, marginTop: 8 }} data-testid="share-section">
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>Share Access with Roles (optional)</Typography>
                {share.phase === "loading" && <ShareBusy phase="loading" count={0} />}
                {share.phase === "unavailable" && <Typography sx={{ fontSize: 14 }}>Sharing is not available.</Typography>}
                {(share.phase === "error" || share.phase === "unknown") && (
                  <Fragment>
                    <Typography color="error" sx={{ fontSize: 14 }}>{share.message}</Typography>
                    {share.phase === "error" && (
                      <Button size="small" color="secondary" onClick={share.reload}>
                        Try again
                      </Button>
                    )}
                    <Typography sx={{ fontSize: 13, color: "#666" }}>You can share it later from the solution's menu.</Typography>
                  </Fragment>
                )}
                {(share.phase === "choose" || share.phase === "confirm" || busy) && (
                  <ShareTable
                    targets={share.targets}
                    notOffered={share.notOffered}
                    notOfferedCount={share.notOfferedCount}
                    grantAll={share.grantAll}
                    selected={share.selected}
                    onSelect={share.setSelected}
                    maxHeight="max(200px, calc(100vh - 640px))"
                  />
                )}
                {shared && <ShareResults results={share.results} />}
              </div>
            )}
          </Fragment>
        ) : (
          <Fragment>
            <CircularProgress size="8rem" style={{ color }} />
            <p style={{ color }}>
              <b>Creating Solution...</b>
            </p>
          </Fragment>
        )}
      </div>
      <Divider />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          position: "relative",
          top: "10px",
          gap: "8px",
        }}
      >
        <Button
          color="secondary"
          disabled={!created || busy}
          onClick={() => {
            dispatch(resetService());
          }}
        >
          Reset
        </Button>
        {!canShare && (
          <Button color="secondary" disabled={!created} onClick={finish}>
            Home
          </Button>
        )}
        {canShare && shared && share.retryIds.length > 0 && (
          <Button color="secondary" onClick={share.retry}>
            Retry
          </Button>
        )}
        {canShare && (
          <Button color="secondary" disabled={busy} onClick={finish}>
            {sentAny ? "Finish" : "Finish without sharing"}
          </Button>
        )}
        {canShare && !shared && (
          <Button variant="contained" color="secondary" disabled={share.phase !== "choose" || !share.selected.length} onClick={share.openConfirm}>
            {`Share Access with Roles (${share.selected.length})`}
          </Button>
        )}
      </div>
      <Dialog
        open={share.phase === "confirm" || busy}
        onClose={() => {
          if (!busy) share.cancelConfirm();
        }}
        disableEscapeKeyDown={busy}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{`Share Solution Access: ${serviceValue.name || "solution"}`}</DialogTitle>
        <DialogContent>
          {busy ? (
            <ShareBusy phase="sharing" count={share.sending} />
          ) : (
            <ShareConfirmation
              targets={share.targets}
              selected={share.selected}
              results={share.results}
              confirmAgain={share.confirmAgain}
              grantAll={share.grantAll}
              serviceName={serviceValue.name}
            />
          )}
        </DialogContent>
        {!busy && (
          <DialogActions>
            <Button onClick={share.cancelConfirm}>Cancel</Button>
            <Button variant="contained" color="secondary" disabled={!share.selected.length} onClick={share.share}>
              Confirm
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </div>
  );
}
